import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  MdClose,
  MdRefresh,
  MdSearch,
  MdCheckCircle,
  MdErrorOutline,
  MdAccessTime,
  MdArrowBack,
  MdContentCopy,
  MdCheck,
  MdListAlt,
} from "react-icons/md";
import {
  getPlaUploadStatusService,
  getPlaAllBatchesService,
} from "../../../core/services/plaFeedServices";
import toast from "react-hot-toast";

interface PlaFeedUploadStatusModalProps {
  initialBatchId?: string;
  onClose: () => void;
}

interface BatchListItem {
  batchId: string;
  fileName: string;
  uploadedBy: string;
  totalRows: number;
  insertedRows: number;
  failedRows: number;
  syncedRows: number;
  status: string;
  errorMessage?: string;
  createdAt: number | string | null;
  completedAt?: number | string | null;
}

interface BatchesPagination {
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  isFirst: boolean;
  isLast: boolean;
}

interface ProductStatusItem {
  msn: string;
  status: string;
  action: string;
  dbSynced: boolean;
  dbSyncedAt: number | string | null;
  priceEngineSynced: boolean;
  priceEngineSyncedAt: number | string | null;
}

interface BatchStatusData {
  batchId: string;
  fileName?: string;
  fileStatus?: string;
  syncStatus?: string;
  totalRows?: number;
  dbSavedCount?: number;
  fullySyncedCount?: number;
  partiallySyncedCount?: number;
  failedCount?: number;
  createdAt?: number | string | null;
  completedAt?: number | string | null;
  pagination?: {
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    isFirst: boolean;
    isLast: boolean;
  };
  products?: ProductStatusItem[];
}

const formatTimestamp = (ts?: string | number | null): string => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return String(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const timeAgo = (ts?: string | number | null): string => {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const PlaFeedUploadStatusModal: React.FC<PlaFeedUploadStatusModalProps> = ({
  initialBatchId = "",
  onClose,
}) => {
  // Mode: "BATCH_LIST" (all uploaded batches) or "BATCH_DETAIL" (individual batch sync inspection)
  const [viewMode, setViewMode] = useState<"BATCH_LIST" | "BATCH_DETAIL">(
    initialBatchId ? "BATCH_DETAIL" : "BATCH_LIST"
  );

  // ---------------- Master List State ----------------
  const [batches, setBatches] = useState<BatchListItem[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchesPagination, setBatchesPagination] = useState<BatchesPagination | null>(null);
  const [batchPageNumber, setBatchPageNumber] = useState(0);
  const [batchPageSize, setBatchPageSize] = useState(10);
  const [batchSearchQuery, setBatchSearchQuery] = useState("");
  const [copiedBatchId, setCopiedBatchId] = useState<string | null>(null);

  // ---------------- Detail State ----------------
  const defaultBatchId = initialBatchId || localStorage.getItem("lastPlaBatchId") || "";
  const [batchIdInput, setBatchIdInput] = useState(defaultBatchId);
  const [activeBatchId, setActiveBatchId] = useState(defaultBatchId);
  const [detailLoading, setDetailLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [statusData, setStatusData] = useState<BatchStatusData | null>(null);

  // Detail Filters & Pagination
  const [activeTab, setActiveTab] = useState<"ALL" | "FULLY_SYNCED" | "PARTIALLY_SYNCED">("ALL");
  const [productPageNumber, setProductPageNumber] = useState(0);
  const [productPageSize, setProductPageSize] = useState(20);
  const [msnSearch, setMsnSearch] = useState("");

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // ---------------- Fetch All Batches ----------------
  const fetchBatches = useCallback(async (page: number, size: number) => {
    setBatchesLoading(true);
    try {
      const res = await getPlaAllBatchesService({ pageNumber: page, pageSize: size });
      if (res?.data?.status && res?.data?.data) {
        setBatches(res.data.data.batches || []);
        setBatchesPagination(res.data.data.pagination || null);
      } else {
        toast.error(res?.data?.statusDescription || "Failed to fetch batches.");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.statusDescription || err?.message || "Failed to load upload history.");
    } finally {
      setBatchesLoading(false);
    }
  }, []);

  // ---------------- Fetch Detail Batch Status ----------------
  const fetchStatus = useCallback(
    async (batchIdToFetch: string, page: number, size: number, statusFilter: string, msnQuery: string) => {
      if (!batchIdToFetch && !msnQuery) return;
      setDetailLoading(true);
      try {
        const res = await getPlaUploadStatusService({
          batchId: batchIdToFetch ? batchIdToFetch.trim() : undefined,
          msn: msnQuery ? msnQuery.trim() : undefined,
          pageNumber: page,
          pageSize: size,
          status: statusFilter,
          includeProducts: true,
        });

        if (res?.data?.status && res?.data?.data) {
          setStatusData(res.data.data);
          if (batchIdToFetch) {
            localStorage.setItem("lastPlaBatchId", batchIdToFetch.trim());
          }
        } else {
          toast.error(res?.data?.statusDescription || "Batch not found.");
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.statusDescription || err?.message || "Failed to fetch status.");
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  // Light poll summary for in-progress uploads
  const pollSummary = useCallback(async (batchIdToPoll: string) => {
    if (!batchIdToPoll) return;
    try {
      const res = await getPlaUploadStatusService({
        batchId: batchIdToPoll.trim(),
        includeProducts: false,
      });
      if (res?.data?.status && res?.data?.data) {
        const pollData = res.data.data;
        setStatusData((prev) => (prev ? { ...prev, ...pollData, products: prev.products } : pollData));
        if (pollData.fileStatus === "COMPLETED" || pollData.fileStatus === "FAILED") {
          setPolling(false);
          fetchStatus(batchIdToPoll, 0, productPageSize, activeTab, msnSearch);
        }
      }
    } catch {
      // Ignore background poll errors
    }
  }, [fetchStatus, productPageSize, activeTab, msnSearch]);

  // Load Batches when in BATCH_LIST view
  useEffect(() => {
    if (viewMode === "BATCH_LIST") {
      fetchBatches(batchPageNumber, batchPageSize);
    }
  }, [viewMode, batchPageNumber, batchPageSize, fetchBatches]);

  // Load Detail when in BATCH_DETAIL view
  useEffect(() => {
    if (viewMode === "BATCH_DETAIL" && activeBatchId) {
      fetchStatus(activeBatchId, productPageNumber, productPageSize, activeTab, msnSearch);
    }
  }, [viewMode, activeBatchId, productPageNumber, productPageSize, activeTab, fetchStatus]);

  // Polling management for active batch
  useEffect(() => {
    if (viewMode === "BATCH_DETAIL" && statusData?.fileStatus === "IN_PROGRESS" && activeBatchId) {
      setPolling(true);
      pollIntervalRef.current = setInterval(() => {
        pollSummary(activeBatchId);
      }, 3000);
    } else {
      setPolling(false);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [viewMode, statusData?.fileStatus, activeBatchId, pollSummary]);

  // Drill down into a specific batch
  const openBatchDetail = (batchId: string) => {
    setActiveBatchId(batchId);
    setBatchIdInput(batchId);
    setProductPageNumber(0);
    setStatusData(null);
    setViewMode("BATCH_DETAIL");
  };

  // Back to All Batches
  const backToBatchList = () => {
    setViewMode("BATCH_LIST");
    fetchBatches(batchPageNumber, batchPageSize);
  };

  const handleSearchBatch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!batchIdInput.trim()) {
      toast.error("Please enter a Batch ID.");
      return;
    }
    setActiveBatchId(batchIdInput.trim());
    setProductPageNumber(0);
    setViewMode("BATCH_DETAIL");
    fetchStatus(batchIdInput.trim(), 0, productPageSize, activeTab, msnSearch);
  };

  const handleCopyBatchId = (batchId: string) => {
    navigator.clipboard.writeText(batchId);
    setCopiedBatchId(batchId);
    toast.success("Batch ID copied!");
    setTimeout(() => setCopiedBatchId(null), 2000);
  };

  const handleTabChange = (tab: "ALL" | "FULLY_SYNCED" | "PARTIALLY_SYNCED") => {
    setActiveTab(tab);
    setProductPageNumber(0);
    if (activeBatchId) {
      fetchStatus(activeBatchId, 0, productPageSize, tab, msnSearch);
    }
  };

  // Filtered batch list for local search
  const filteredBatches = batches.filter((b) => {
    if (!batchSearchQuery.trim()) return true;
    const q = batchSearchQuery.toLowerCase();
    return (
      (b.batchId && b.batchId.toLowerCase().includes(q)) ||
      (b.fileName && b.fileName.toLowerCase().includes(q)) ||
      (b.uploadedBy && b.uploadedBy.toLowerCase().includes(q))
    );
  });

  const totalRows = statusData?.totalRows || 0;
  const fullySynced = statusData?.fullySyncedCount || 0;
  const partiallySynced = statusData?.partiallySyncedCount || 0;
  const dbSaved = statusData?.dbSavedCount || 0;
  const syncPct = totalRows > 0 ? Math.min(100, Math.round((fullySynced / totalRows) * 100)) : 0;
  const pagination = statusData?.pagination;
  const products = statusData?.products || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-200">

        {/* Modal Header */}
        <div className="px-6 py-4 bg-white border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {viewMode === "BATCH_DETAIL" && (
              <button
                onClick={backToBatchList}
                className="p-1.5 hover:bg-gray-100 rounded-md text-[#43425D] flex items-center gap-1 text-xs font-semibold border border-gray-200 transition-colors"
                title="Back to All Batches"
              >
                <MdArrowBack size={16} />
                All Batches
              </button>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold tracking-wide m-0" style={{ color: "#43425D" }}>
                  {viewMode === "BATCH_LIST" ? "PLA Feed Upload History" : "Batch Sync Status"}
                </h2>
                {viewMode === "BATCH_DETAIL" && polling && (
                  <span className="flex items-center gap-1 text-[11px] bg-blue-50 px-2.5 py-0.5 rounded-full border border-blue-200 text-blue-700 font-medium">
                    <span className="w-2 h-2 rounded-full bg-blue-500 animate-ping" />
                    Live Syncing...
                  </span>
                )}
              </div>
              <p className="text-xs mt-0.5" style={{ color: "#6b7280" }}>
                {viewMode === "BATCH_LIST"
                  ? "Audit log of all uploaded PLA feed files and their price-engine sync status"
                  : `Real-time synchronization tracking for Batch: ${activeBatchId}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (viewMode === "BATCH_LIST") {
                  fetchBatches(batchPageNumber, batchPageSize);
                } else if (activeBatchId) {
                  fetchStatus(activeBatchId, productPageNumber, productPageSize, activeTab, msnSearch);
                }
              }}
              title="Refresh"
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-[#43425D]"
            >
              <MdRefresh
                size={20}
                className={(batchesLoading || detailLoading) ? "animate-spin" : ""}
              />
            </button>
            <button
              onClick={onClose}
              title="Close"
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-800"
            >
              <MdClose size={20} />
            </button>
          </div>
        </div>

        {/* ================= VIEW 1: MASTER BATCH LIST ================= */}
        {viewMode === "BATCH_LIST" ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <div className="relative max-w-xs flex-1">
                <input
                  type="text"
                  value={batchSearchQuery}
                  onChange={(e) => setBatchSearchQuery(e.target.value)}
                  placeholder="Search file name, batch ID, or user..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-[#43425D] focus:border-[#43425D] outline-none text-gray-800 bg-white"
                />
                <MdSearch className="absolute left-2.5 top-2 text-gray-400 text-sm" />
              </div>

              {/* Direct Batch ID Lookup */}
              <form onSubmit={handleSearchBatch} className="flex items-center gap-2">
                <input
                  type="text"
                  value={batchIdInput}
                  onChange={(e) => setBatchIdInput(e.target.value)}
                  placeholder="Enter specific Batch ID..."
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-[#43425D] focus:border-[#43425D] outline-none text-gray-800 bg-white w-56"
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 text-xs font-semibold rounded transition-colors"
                  style={{ backgroundColor: "#43425D", color: "#ffffff" }}
                >
                  Inspect Batch
                </button>
              </form>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="overflow-x-auto rounded border border-gray-200 shadow-sm">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b border-gray-200 uppercase text-[11px] tracking-wider font-semibold">
                      <th className="px-4 py-3 font-bold" style={{ color: "#43425D" }}>Uploaded At</th>
                      <th className="px-4 py-3 font-bold" style={{ color: "#43425D" }}>File Name</th>
                      <th className="px-4 py-3 font-bold" style={{ color: "#43425D" }}>Batch ID</th>
                      <th className="px-4 py-3 font-bold" style={{ color: "#43425D" }}>Uploaded By</th>
                      <th className="px-4 py-3 font-bold text-center" style={{ color: "#43425D" }}>Total Rows</th>
                      <th className="px-4 py-3 font-bold text-center" style={{ color: "#43425D" }}>Status</th>
                      <th className="px-4 py-3 font-bold text-center" style={{ color: "#43425D" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {batchesLoading && !batches.length ? (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-gray-500">
                          <span
                            className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full animate-spin mr-2"
                            style={{ borderColor: "#43425D", borderTopColor: "transparent" }}
                          />
                          Loading upload history...
                        </td>
                      </tr>
                    ) : !filteredBatches.length ? (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-gray-500">
                          <MdListAlt size={36} className="mx-auto mb-2 text-gray-400" />
                          <p className="font-medium">No uploaded batches found.</p>
                          <p className="text-xs text-gray-400 mt-1">Upload a PLA Feed file to start monitoring batch synchronization.</p>
                        </td>
                      </tr>
                    ) : (
                      filteredBatches.map((b) => (
                        <tr key={b.batchId} className="hover:bg-gray-50 transition-colors [&:nth-child(odd)]:bg-[#fafafa]">
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                            <div className="font-medium">{formatTimestamp(b.createdAt)}</div>
                            <div className="text-[10px] text-gray-400">{timeAgo(b.createdAt)}</div>
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-800 max-w-[200px] truncate" title={b.fileName}>
                            {b.fileName || "—"}
                          </td>
                          <td className="px-4 py-3 font-mono text-[11px] text-gray-600">
                            <div className="flex items-center gap-1.5">
                              <span title={b.batchId}>{b.batchId.slice(0, 13)}...</span>
                              <button
                                onClick={() => handleCopyBatchId(b.batchId)}
                                className="p-0.5 text-gray-400 hover:text-gray-700 rounded"
                                title="Copy Batch ID"
                              >
                                {copiedBatchId === b.batchId ? (
                                  <MdCheck className="text-green-600" size={14} />
                                ) : (
                                  <MdContentCopy size={14} />
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-xs truncate max-w-[180px]" title={b.uploadedBy}>
                            {b.uploadedBy || "System"}
                          </td>
                          <td className="px-4 py-3 font-bold text-center text-[#43425D]">
                            {b.totalRows ?? 0}
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                                b.status === "COMPLETED"
                                  ? "bg-green-100 text-green-800 border border-green-200"
                                  : b.status === "FAILED"
                                  ? "bg-red-100 text-red-800 border border-red-200"
                                  : "bg-blue-100 text-blue-800 border border-blue-200 animate-pulse"
                              }`}
                            >
                              {b.status || "IN_PROGRESS"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            <button
                              onClick={() => openBatchDetail(b.batchId)}
                              className="px-3 py-1 bg-white border border-[#43425D] text-[#43425D] hover:bg-[#43425D] hover:text-white rounded text-xs font-semibold shadow-sm transition-all"
                            >
                              View Status &rarr;
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Master Pagination Controls */}
              {batchesPagination && batchesPagination.totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600 pt-4">
                  <div className="flex items-center gap-2">
                    <span>Batches per page:</span>
                    <select
                      value={batchPageSize}
                      onChange={(e) => {
                        const newSize = Number(e.target.value);
                        setBatchPageSize(newSize);
                        setBatchPageNumber(0);
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-xs outline-none bg-white text-gray-800"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-gray-400">|</span>
                    <span>
                      Page {batchesPagination.pageNumber + 1} of {batchesPagination.totalPages} ({batchesPagination.totalElements} total uploads)
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setBatchPageNumber((p) => Math.max(0, p - 1))}
                      disabled={batchesPagination.isFirst || batchesLoading}
                      className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-gray-700"
                    >
                      &larr; Previous
                    </button>
                    <button
                      onClick={() => setBatchPageNumber((p) => Math.min(batchesPagination.totalPages - 1, p + 1))}
                      disabled={batchesPagination.isLast || batchesLoading}
                      className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-gray-700"
                    >
                      Next &rarr;
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ================= VIEW 2: BATCH SYNC DETAIL ================= */
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Batch Detail Toolbar */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <form onSubmit={handleSearchBatch} className="flex items-center gap-2 flex-1 max-w-md">
                <input
                  type="text"
                  value={batchIdInput}
                  onChange={(e) => setBatchIdInput(e.target.value)}
                  placeholder="Enter Batch ID (e.g. c37e78c7-ed1b-...)"
                  className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-[#43425D] focus:border-[#43425D] outline-none text-gray-800 bg-white"
                />
                <button
                  type="submit"
                  disabled={detailLoading}
                  className="px-3 py-1.5 text-xs font-semibold rounded transition-colors disabled:opacity-50"
                  style={{ backgroundColor: "#43425D", color: "#ffffff" }}
                >
                  Check Status
                </button>
              </form>

              {activeBatchId && (
                <div className="text-xs text-gray-600 flex items-center gap-2">
                  <span className="font-semibold text-gray-700">Active Batch:</span>
                  <code
                    className="bg-gray-200 border border-gray-300 px-1.5 py-0.5 rounded text-[11px] font-mono font-semibold"
                    style={{ color: "#43425D" }}
                  >
                    {activeBatchId}
                  </code>
                </div>
              )}
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {!statusData && !detailLoading ? (
                <div className="text-center py-16 text-gray-500 text-sm">
                  <MdAccessTime size={42} className="mx-auto mb-2 text-gray-400" />
                  <p className="font-medium">No batch details loaded.</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Enter a Batch ID above or return to the all batches list.
                  </p>
                </div>
              ) : (
                <>
                  {/* Metric Cards Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                    <div className="bg-gray-50 border border-gray-200 rounded p-3 text-center">
                      <p className="text-[11px] font-semibold uppercase text-gray-500">File Status</p>
                      <p className="mt-1">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${
                            statusData?.fileStatus === "COMPLETED"
                              ? "bg-green-100 text-green-800 border border-green-200"
                              : statusData?.fileStatus === "FAILED"
                              ? "bg-red-100 text-red-800 border border-red-200"
                              : "bg-blue-100 text-blue-800 border border-blue-200 animate-pulse"
                          }`}
                        >
                          {statusData?.fileStatus || "PENDING"}
                        </span>
                      </p>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded p-3 text-center">
                      <p className="text-[11px] font-semibold uppercase text-gray-500">Sync Status</p>
                      <p className="mt-1">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${
                            statusData?.syncStatus === "FULLY_SYNCED"
                              ? "bg-green-100 text-green-800 border border-green-200"
                              : statusData?.syncStatus === "PARTIALLY_SYNCED"
                              ? "bg-amber-100 text-amber-800 border border-amber-200"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {statusData?.syncStatus || "IN_PROGRESS"}
                        </span>
                      </p>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded p-3 text-center">
                      <p className="text-[11px] font-semibold uppercase text-gray-500">Total Rows</p>
                      <p className="text-lg font-bold mt-0.5" style={{ color: "#43425D" }}>{totalRows}</p>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded p-3 text-center">
                      <p className="text-[11px] font-semibold uppercase text-gray-500">CMS DB Saved</p>
                      <p className="text-lg font-bold text-gray-800 mt-0.5">{dbSaved}</p>
                    </div>

                    <div className="bg-green-50/60 border border-green-200 rounded p-3 text-center">
                      <p className="text-[11px] font-semibold uppercase text-green-700">Fully Synced</p>
                      <p className="text-lg font-bold text-green-800 mt-0.5">{fullySynced}</p>
                    </div>

                    <div className="bg-amber-50/60 border border-amber-200 rounded p-3 text-center">
                      <p className="text-[11px] font-semibold uppercase text-amber-700">Partially Synced</p>
                      <p className="text-lg font-bold text-amber-800 mt-0.5">{partiallySynced}</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="bg-gray-100 rounded-full h-3 overflow-hidden border border-gray-200 relative">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-green-600 transition-all duration-500 rounded-full"
                      style={{ width: `${syncPct}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] text-gray-500 -mt-3 px-1">
                    <span>Price Engine Sync: {syncPct}% complete</span>
                    {statusData?.completedAt && (
                      <span>Completed: {formatTimestamp(statusData.completedAt)} ({timeAgo(statusData.completedAt)})</span>
                    )}
                  </div>

                  {/* Filter Tabs & Search */}
                  <div className="flex flex-wrap items-center justify-between border-b border-gray-200 pb-2 pt-1 gap-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleTabChange("ALL")}
                        className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                          activeTab === "ALL"
                            ? "text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                        style={activeTab === "ALL" ? { backgroundColor: "#43425D", color: "#ffffff" } : undefined}
                      >
                        All Products ({dbSaved})
                      </button>
                      <button
                        onClick={() => handleTabChange("FULLY_SYNCED")}
                        className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                          activeTab === "FULLY_SYNCED"
                            ? "bg-green-700 text-white"
                            : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                        }`}
                      >
                        Fully Synced ({fullySynced})
                      </button>
                      <button
                        onClick={() => handleTabChange("PARTIALLY_SYNCED")}
                        className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                          activeTab === "PARTIALLY_SYNCED"
                            ? "bg-amber-600 text-white"
                            : "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
                        }`}
                      >
                        Partially Synced ({partiallySynced})
                      </button>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <input
                          type="text"
                          value={msnSearch}
                          onChange={(e) => setMsnSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              fetchStatus(activeBatchId, 0, productPageSize, activeTab, msnSearch);
                            }
                          }}
                          placeholder="Filter MSN..."
                          className="pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#43425D] w-36 text-gray-800 bg-white"
                        />
                        <MdSearch className="absolute left-2 top-1.5 text-gray-400 text-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Products Table */}
                  <div className="overflow-x-auto rounded border border-gray-200">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-100 border-b border-gray-200 uppercase text-[11px] tracking-wider font-semibold">
                          <th className="px-4 py-2.5 font-bold" style={{ color: "#43425D" }}>MSN</th>
                          <th className="px-4 py-2.5 font-bold" style={{ color: "#43425D" }}>Sync Status</th>
                          <th className="px-4 py-2.5 font-bold" style={{ color: "#43425D" }}>Action</th>
                          <th className="px-4 py-2.5 font-bold" style={{ color: "#43425D" }}>CMS DB Saved</th>
                          <th className="px-4 py-2.5 font-bold" style={{ color: "#43425D" }}>Price Engine Synced</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {detailLoading && !products.length ? (
                          <tr>
                            <td colSpan={5} className="py-8 text-center text-gray-500">
                              <span
                                className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full animate-spin mr-2"
                                style={{ borderColor: "#43425D", borderTopColor: "transparent" }}
                              />
                              Loading products...
                            </td>
                          </tr>
                        ) : !products.length ? (
                          <tr>
                            <td colSpan={5} className="py-8 text-center text-gray-500">
                              No products found matching the filter.
                            </td>
                          </tr>
                        ) : (
                          products.map((item, idx) => (
                            <tr key={item.msn || idx} className="hover:bg-gray-50 transition-colors [&:nth-child(odd)]:bg-[#fbfbfb]">
                              <td className="px-4 py-2.5 font-medium font-mono" style={{ color: "#43425D" }}>
                                {item.msn}
                              </td>
                              <td className="px-4 py-2.5">
                                {item.status === "FULLY_SYNCED" ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 border border-green-200">
                                    <MdCheckCircle className="text-green-600" />
                                    FULLY SYNCED
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                    <MdErrorOutline className="text-amber-600" />
                                    PARTIALLY SYNCED
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2.5">
                                <span
                                  className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                    item.action === "INSERT"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-blue-100 text-blue-800"
                                  }`}
                                >
                                  {item.action || "UPDATE"}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-gray-600">
                                {item.dbSynced ? (
                                  <span title={formatTimestamp(item.dbSyncedAt)}>
                                    ✓ {formatTimestamp(item.dbSyncedAt)}
                                  </span>
                                ) : (
                                  <span className="text-red-500">✕ Pending</span>
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-gray-600">
                                {item.priceEngineSynced ? (
                                  <span title={formatTimestamp(item.priceEngineSyncedAt)} className="text-green-700 font-medium">
                                    ✓ {formatTimestamp(item.priceEngineSyncedAt)}
                                  </span>
                                ) : (
                                  <span className="text-amber-600 font-medium">
                                    ⏳ Pending sync
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Product Pagination Controls Footer */}
                  {pagination && pagination.totalPages > 1 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600 pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <span>Rows per page:</span>
                        <select
                          value={productPageSize}
                          onChange={(e) => {
                            const newSize = Number(e.target.value);
                            setProductPageSize(newSize);
                            setProductPageNumber(0);
                          }}
                          className="border border-gray-300 rounded px-2 py-1 text-xs outline-none bg-white text-gray-800"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span className="text-gray-400">|</span>
                        <span>
                          Page {pagination.pageNumber + 1} of {pagination.totalPages} ({pagination.totalElements} total products)
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setProductPageNumber((p) => Math.max(0, p - 1))}
                          disabled={pagination.isFirst || detailLoading}
                          className="px-2.5 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-gray-700"
                        >
                          &larr; Previous
                        </button>
                        <button
                          onClick={() => setProductPageNumber((p) => Math.min(pagination.totalPages - 1, p + 1))}
                          disabled={pagination.isLast || detailLoading}
                          className="px-2.5 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-gray-700"
                        >
                          Next &rarr;
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Modal Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-semibold rounded hover:bg-[#34334a] transition-colors"
            style={{ backgroundColor: "#43425D", color: "#ffffff" }}
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
};

export default PlaFeedUploadStatusModal;
